// @flow
import React, { Component, type Node } from 'react';
import classnames from 'classnames';
import { noop } from 'lodash';

import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import { ICONS } from 'Components/utils/icon/icons';

import { Icon } from 'Components/frameworks/icon/Icon.jsx';

import { themes } from './InputNumber.themes';

type Props = {
  ariaLabel?: string,
  autoComplete?: AutoComplete,
  className?: string,
  dataTestId?: string,
  hasColumnLayout?: boolean,
  id?: string,
  isDisabled?: boolean,
  isLoading?: boolean,
  isReadOnly?: boolean,
  isRequired?: boolean,
  label?: string | Node,
  attendeeType?: string,
  max?: number,
  min?: number,
  name?: string,
  notes?: string,
  onBlur?: (SyntheticEvent<>) => void,
  onChange?: (name: string, value: number) => void,
  onClick?: (SyntheticEvent<>) => void,
  onFocus?: (SyntheticEvent<>) => void,
  onKeyDown?: (SyntheticKeyboardEvent<>) => void,
  onKeyUp?: (SyntheticKeyboardEvent<>) => void,
  placeholder?: number,
  step?: number,
  theme?: string,
  validationState?: string,
  value?: ?number,
};

type State = {
  value: ?number,
};

export class InputNumber extends Component<Props, State> {
  static defaultProps = {
    autoComplete: 'off',
    max: 10,
    min: 0,
    step: 1,
    theme: 'default',
    validationState: 'NOT_VALIDATED',
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      value: props.value,
    };
  }

  UNSAFE_componentWillReceiveProps({ value }: Props) {
    this.setState(prevState => {
      if (prevState.value !== value) {
        return { value };
      }
    });
  }

  onChange = (e: SyntheticEvent<>) => {
    const { isLoading, isReadOnly, name = '', onChange = noop } = this.props;

    if (!isReadOnly && !isLoading) {
      e.persist();

      // $FlowFixMe
      const value = Number(e.target.value);

      this.setState(
        {
          value,
        },
        () => onChange(name, value),
      );
    }
  };

  decrease = () => {
    const { name = '', min = 0, step = 1, onChange = noop } = this.props;

    const currentValue = this.state.value || min;
    const decreasedValue = currentValue - step;
    const nextValue = decreasedValue >= min ? decreasedValue : min;

    this.setState({
      value: nextValue,
    });

    onChange(name, nextValue);
  };

  increase = () => {
    const { name = '', min = 0, max = 10, step = 1, onChange = noop } = this.props;

    const currentValue = this.state.value || min;
    const increasedValue = currentValue + step;
    const nextValue = increasedValue <= max ? increasedValue : max;

    this.setState({
      value: nextValue,
    });

    onChange(name, nextValue);
  };

  render() {
    const {
      ariaLabel,
      autoComplete,
      className,
      dataTestId,
      hasColumnLayout,
      id = generateUniqueId(
        [typeof this.props.label === 'string' ? this.props.label : ''],
        'InputNumber_',
      ),
      isDisabled,
      isLoading,
      isReadOnly,
      isRequired,
      label,
      attendeeType,
      max = 10,
      min = 1,
      name,
      notes,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      onKeyUp,
      placeholder,
      step,
      theme,
      validationState,
    } = this.props;

    const { value } = this.state;

    const hasValue = Boolean(value);
    const decreaseDisabled = isDisabled || isLoading || Number(value) <= min;
    const increaseDisabled = isDisabled || isLoading || Number(value) >= max;

    const {
      thButton,
      thButtonEnabled,
      thButtonIconWidth,
      thButtonStyle,
      thInput,
      thInputs,
      thLabel,
      thNotes,
      thRoot,
    } = themes[theme]({ hasColumnLayout, hasValue, isDisabled, isReadOnly, validationState });

    return (
      <div className={classnames(className, thRoot)} data-testid={dataTestId}>
        {!!label && (
          <label className={thLabel} htmlFor={id}>
            {label}
            {!!notes && <div className={thNotes}>{notes}</div>}
          </label>
        )}
        <div className={thInputs}>
          {!isReadOnly && (
            <button
              className={classnames(thButton, { [thButtonEnabled]: !decreaseDisabled })}
              disabled={decreaseDisabled}
              onClick={this.decrease}
              style={thButtonStyle}
              type="button"
              data-testid={attendeeType ? `${attendeeType}DecrementButton` : 'DecrementButton'}
            >
              <Icon name={ICONS.MINUS} width={thButtonIconWidth} />
            </button>
          )}
          <input
            aria-label={ariaLabel}
            autoComplete={autoComplete}
            className={thInput}
            disabled={isDisabled}
            id={id}
            max={max}
            min={min}
            // $FlowFixMe
            {...(name ? { name } : null)}
            onBlur={onBlur}
            onChange={this.onChange}
            onClick={onClick}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            {...(placeholder ? { placeholder: placeholder } : null)}
            readOnly
            required={isRequired}
            step={step}
            style={{
              width: `${max.toString().length + 1}em`,
              height: '1.875em',
              lineHeight: '1.875em',
            }}
            type="number"
            value={value}
          />
          {!isReadOnly && (
            <button
              className={classnames(thButton, { [thButtonEnabled]: !increaseDisabled })}
              disabled={increaseDisabled}
              onClick={this.increase}
              style={thButtonStyle}
              type="button"
              data-testid={attendeeType ? `${attendeeType}IncrementButton` : 'IncrementButton'}
            >
              <Icon name={ICONS.PLUS} width={thButtonIconWidth} />
            </button>
          )}
        </div>
      </div>
    );
  }
}
